import { cn } from '@/lib/utils';
import { useDynamicLocale } from '@/hoslog-intl';
import * as React from 'react';
import { useController } from 'react-hook-form';
import { Option, MultipleSelector as Root } from 'ui/multiselect';
import { useFormField } from '.';
import { FormField } from './Field';
import { UseFormFieldProps } from './type';

type Props = UseFormFieldProps &
  Omit<React.ComponentProps<typeof Root>, 'value' | 'onChange' | 'defaultOptions' | 'options'> & {
    name: string;

    // the selectable list (encrypted id in value)
    options: Option[];

    // if you prefer defaultOptions naming from your usage
    defaultOptions?: Option[];
  };

const MultiSelect = (props: Props) => {
  const { formFieldProps, childProps, error } = useFormField(props);
  const { name, options, defaultOptions } = props;
  const { locale } = useDynamicLocale();

  const localizedOptions = React.useMemo(
    () =>
      options.map((option) => ({
        ...option,
        label: locale === 'en' && typeof option.labelEng === 'string' ? option.labelEng : option.label,
      })),
    [locale, options],
  );

  const localizedDefaultOptions = React.useMemo(
    () =>
      (defaultOptions ?? options).map((option) => ({
        ...option,
        label: locale === 'en' && typeof option.labelEng === 'string' ? option.labelEng : option.label,
      })),
    [defaultOptions, locale, options],
  );

  const { field } = useController({
    name,
    // helps RHF avoid undefined and narrows behavior
    defaultValue: [] as unknown, // keep as unknown because RHF types might be loose in your wrapper
  });

  const selectedOptions = React.useMemo<Option[]>(() => {
    // ✅ sanitize field.value into string[]
    const ids = Array.isArray(field.value)
      ? (field.value as unknown[]).filter((v): v is string => typeof v === 'string')
      : [];

    const set = new Set(ids);
    return localizedOptions.filter((o) => set.has(o.value));
  }, [field.value, localizedOptions]);

  return (
    <FormField {...formFieldProps}>
      <Root
        {...childProps}
        options={localizedOptions}
        defaultOptions={localizedDefaultOptions}
        value={selectedOptions}
        onChange={(opts) => field.onChange(opts.map((o) => o.value))}
        hidePlaceholderWhenSelected
        aria-invalid={!!error}
        className={cn(childProps.className, error && 'border-destructive focus-visible:ring-destructive')}
      />
    </FormField>
  );
};

export default MultiSelect;
